import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
const API_URL = `${BACKEND_URL}/api`;

axios.defaults.withCredentials = true;

// Global axios interceptor to handle 401 errors
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      // Token is invalid or expired - need to re-authenticate
      console.warn('Authentication failed (401) - token invalid or expired');

      // Trigger re-authentication event
      window.dispatchEvent(new CustomEvent('auth-failed', {
        detail: { reason: 'token_expired' }
      }));
    }
    return Promise.reject(error);
  }
);

const getAuthHeaders = (token, workspaceId = null) => {
  const headers = {
    'Authorization': `Bearer ${token}`
  };

  if (workspaceId) {
    headers['x-workspace-id'] = workspaceId;
  }

  return headers;
};

export const api = {
  checkSession: () => 
    axios.get(`${API_URL}/session`),
  
  fetchChannels: (token, workspaceId = null) =>
    axios.get(`${API_URL}/channels`, {
      headers: getAuthHeaders(token, workspaceId),
      withCredentials: true
    }),
  
  fetchMessages: (channelId, token, workspaceId = null) =>
    axios.get(`${API_URL}/messages/${channelId}`, {
      headers: getAuthHeaders(token, workspaceId),
      withCredentials: true
    }),
  
  sendMessage: (channel, text, token, mentions = [], workspaceId = null, hubspotSync = null) =>
    axios.post(`${API_URL}/messages`,
      { channel, text, mentions, hubspotSync },
      {
        headers: getAuthHeaders(token, workspaceId),
        withCredentials: true
      }
    ),

  updateMessage: (channelId, ts, text, token, workspaceId = null) =>
    axios.put(`${API_URL}/messages/${channelId}/${ts}`,
      { text },
      {
        headers: getAuthHeaders(token, workspaceId),
        withCredentials: true
      }
    ),

  deleteMessage: (channelId, ts, token, workspaceId = null) =>
    axios.delete(`${API_URL}/messages/${channelId}/${ts}`, {
      headers: getAuthHeaders(token, workspaceId),
      withCredentials: true
    }),

  fetchCurrentUser: (token, workspaceId = null) =>
    axios.get(`${API_URL}/users/me`, {
      headers: getAuthHeaders(token, workspaceId),
      withCredentials: true
    }),

  fetchUsers: (token, workspaceId = null) =>
    axios.get(`${API_URL}/users`, {
      headers: getAuthHeaders(token, workspaceId),
      withCredentials: true
    }),

  fetchUser: (userId, token, workspaceId = null) =>
    axios.get(`${API_URL}/users/${userId}`, {
      headers: getAuthHeaders(token, workspaceId),
      withCredentials: true
    }),

  fetchChannelMembers: (channelId, token, workspaceId = null) =>
    axios.get(`${API_URL}/channels/${channelId}/members`, {
      headers: getAuthHeaders(token, workspaceId),
      withCredentials: true
    }),

  joinBotToChannel: (channelId, token, workspaceId = null) =>
    axios.post(`${API_URL}/channels/${channelId}/join`, {}, {
      headers: getAuthHeaders(token, workspaceId),
      withCredentials: true
    }),

  fetchThreadReplies: (channelId, threadTs, token, workspaceId = null) =>
    axios.get(`${API_URL}/threads/${channelId}/${threadTs}`, {
      headers: getAuthHeaders(token, workspaceId),
      withCredentials: true
    }),

  sendThreadReply: (channel, text, threadTs, token, workspaceId = null) =>
    axios.post(`${API_URL}/threads/reply`,
      { channel, text, thread_ts: threadTs },
      {
        headers: getAuthHeaders(token, workspaceId),
        withCredentials: true
      }
    ),

  addReaction: (channelId, ts, emoji, token, workspaceId = null) =>
    axios.post(`${API_URL}/messages/${channelId}/${ts}/reactions`,
      { emoji },
      {
        headers: getAuthHeaders(token, workspaceId),
        withCredentials: true
      }
    ),

  removeReaction: (channelId, ts, emoji, token, workspaceId = null) =>
    axios.delete(`${API_URL}/messages/${channelId}/${ts}/reactions/${encodeURIComponent(emoji)}`, {
      headers: getAuthHeaders(token, workspaceId),
      withCredentials: true
    }),

  addBookmark: (channel, timestamp, token) =>
    axios.post(`${API_URL}/bookmarks/add`,
      { token, channel, timestamp },
      {
        headers: getAuthHeaders(token),
        withCredentials: true
      }
    ),

  removeBookmark: (channel, timestamp, token) =>
    axios.post(`${API_URL}/bookmarks/remove`,
      { token, channel, timestamp },
      {
        headers: getAuthHeaders(token),
        withCredentials: true
      }
    ),

  addReminder: (text, time, token) =>
    axios.post(`${API_URL}/reminders/add`,
      { token, text, time },
      {
        headers: getAuthHeaders(token),
        withCredentials: true
      }
    ),

  removeReminder: (id, token) =>
    axios.post(`${API_URL}/reminders/remove`,
      { token, id },
      {
        headers: getAuthHeaders(token),
        withCredentials: true
      }
    ),

  logout: () =>
    axios.post(`${API_URL}/logout`),

  // Workspace management APIs
  fetchWorkspaces: (token) =>
    axios.get(`${API_URL}/workspaces`, {
      headers: getAuthHeaders(token),
      withCredentials: true
    }),

  fetchCurrentWorkspace: (token, workspaceId = null) =>
    axios.get(`${API_URL}/workspaces/current`, {
      headers: getAuthHeaders(token, workspaceId),
      withCredentials: true
    }),

  switchWorkspace: (teamId, token) =>
    axios.post(`${API_URL}/workspaces/switch`,
      { teamId },
      {
        headers: getAuthHeaders(token),
        withCredentials: true
      }
    ),

  disconnectWorkspace: (teamId, token) =>
    axios.delete(`${API_URL}/workspaces/${teamId}`, {
      headers: getAuthHeaders(token),
      withCredentials: true
    }),

  // HubSpot APIs
  createHubSpotContact: (contactData, hubspotPortalId) =>
    axios.post(`${API_URL}/hubspot/create-contact`, contactData, {
      headers: {
        'Content-Type': 'application/json',
        'x-hubspot-portal-id': hubspotPortalId
      },
      withCredentials: true
    })
};